/**
 * Proxy service to help fetch data from CORS-restricted endpoints
 * Uses public CORS proxies as fallbacks when direct fetching fails
 * Now using native fetch for better performance
 */
export const proxyService = {
    // Cache for metadata to avoid repeated CORS requests
    metadataCache: new Map<string, any>(),

    // List of domains that are known to have CORS issues
    corsProblemDomains: [
        'moonitcdn.io',
        'cf-ipfs.com',
        'ipfs.dweb.link',
        'ipfs.moralis.io',
        '5.9.111.106',  // Added per recent errors
        'boop.xyz',     // Added per recent errors
        'raw.githubusercontent.com',
        'arweave.net',
        'githubusercontent.com',
        'ipfs-forward.solanatracker.io', // Added based on recent errors
        'data.solanatracker.io' // Add Solana tracker to the list of domains with CORS issues
    ],

    /**
     * Fetch data from Solana Tracker Wallet API through our local proxy
     * @param walletAddress The wallet address to get data for
     * @returns The wallet data or null if it couldn't be fetched
     */
    async fetchSolanaTrackerWallet(walletAddress: string): Promise<any> {
        try {
            if (!walletAddress) {
                console.error('Invalid wallet address provided');
                return null;
            }

            console.log(`Fetching wallet data for ${walletAddress} through proxy`);
            const API_KEY = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;

            // Use our local proxy endpoint for the request
            const proxyUrl = `/api/proxy/solanatracker/wallet/${walletAddress}`;
            const response = await fetch(proxyUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    // Pass the API key if available
                    ...(API_KEY ? { 'x-api-key': API_KEY } : {})
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error using proxy to fetch Solana Tracker wallet data:', error);
            return null;
        }
    },

    /**
     * Check if a URL is likely to have CORS issues
     */
    hasCorsIssues(url: string): boolean {
        // Check against our list of problematic domains
        return this.corsProblemDomains.some(domain => url.includes(domain));
    },

    /**
     * Fetch metadata from a URL, handling CORS issues with fallback proxies
     * @param url The URL to fetch metadata from
     * @returns The metadata or null if it couldn't be fetched
     */
    async fetchMetadata(url: string): Promise<any> {
        // Check cache first
        if (this.metadataCache.has(url)) {
            console.log(`🔄 Using cached metadata for: ${url.substring(0, 30)}...`);
            return this.metadataCache.get(url);
        }

        // Skip direct fetch for known problematic domains
        if (this.hasCorsIssues(url)) {
            console.log(`⚠️ Skipping direct fetch for CORS-problematic domain: ${url.substring(0, 30)}...`);
            return this.fetchWithProxy(url);
        }

        // First try direct fetch with a short timeout
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1500); // Reduced timeout

            const response = await fetch(url, {
                signal: controller.signal,
                headers: { 'Accept': 'application/json' },
                mode: 'cors' // Explicitly set CORS mode
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            // Cache the result
            this.metadataCache.set(url, data);

            return data;
        } catch (error) {
            // If CORS error or network error, try with proxy
            console.warn(`Direct fetch failed for ${url.substring(0, 30)}... Trying proxies`);
            return this.fetchWithProxy(url);
        }
    },

    /**
     * Fetch data through a CORS proxy
     * @param url The URL to fetch
     * @returns The fetched data or null
     */
    async fetchWithProxy(url: string): Promise<any> {
        // If we already know this URL fails, return null immediately to avoid wasting resources
        if (this.metadataCache.has(url) && this.metadataCache.get(url) === null) {
            console.log(`⚠️ Using cached failure for known problematic URL: ${url.substring(0, 30)}...`);
            return null;
        }

        // List of public CORS proxies to try (in order of preference)
        // Updated with more reliable options first and configuration options
        const proxies = [
            // JsonProxy - more reliable for JSON endpoints
            {
                url: `https://jsonp.afeld.me/?url=${encodeURIComponent(url)}`,
                headers: { 'Accept': 'application/json' }
            },
            // Directly use allorigins which has been more reliable
            {
                url: `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
                headers: { 'Accept': 'application/json' }
            },
            // Try thingproxy as an alternate
            {
                url: `https://thingproxy.freeboard.io/fetch/${url}`,
                headers: { 'Accept': 'application/json' }
            },
            // Try crossorigin.me as another option
            {
                url: `https://crossorigin.me/${url}`,
                headers: { 'Accept': 'application/json' }
            },
            // Try CORS.SH without the problematic header
            {
                url: `https://proxy.cors.sh/${url}`,
                headers: { 'Accept': 'application/json' }
            },
            // Another alternative proxy
            {
                url: `https://cors.bridged.cc/${url}`,
                headers: { 'Accept': 'application/json' }
            }
        ];

        // Try each proxy in sequence until one works
        for (const proxy of proxies) {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);

                console.log(`🔄 Trying proxy: ${proxy.url.split('?')[0]}`);

                const response = await fetch(proxy.url, {
                    signal: controller.signal,
                    headers: proxy.headers,
                    // Don't explicitly set mode to avoid preflight issues
                });

                clearTimeout(timeoutId);

                // If we got a successful response
                if (response.ok) {
                    try {
                        const data = await response.json();

                        // Cache the successful result
                        if (data) {
                            this.metadataCache.set(url, data);
                            console.log(`✅ Successfully fetched via proxy: ${url.substring(0, 30)}...`);
                            return data;
                        }
                    } catch (parseError) {
                        console.warn(`Error parsing proxy response: ${proxy.url.split('?')[0]}`);
                        // Continue to next proxy on parse error
                    }
                }
            } catch (proxyError) {
                // Just continue to the next proxy
                console.warn(`Proxy failed: ${proxy.url.split('?')[0]}`);
                continue;
            }
        }

        // If all proxies failed, fallback to returning empty metadata with minimal required fields
        console.warn(`⛔ All proxies failed for: ${url.substring(0, 30)}...`);

        // Create basic metadata with defaults instead of null
        const fallbackMetadata = {
            name: "Unknown Token",
            symbol: "???",
            description: "Metadata could not be loaded"
        };

        // Cache this fallback so we don't keep trying failed URLs
        this.metadataCache.set(url, fallbackMetadata);
        return fallbackMetadata;
    },

    /**
     * Clear the metadata cache
     */
    clearCache() {
        this.metadataCache.clear();
        console.log('🧹 Metadata cache cleared');
    }
};

export default proxyService; 